'use client';

import Image from '@/components/Image';
import Button from '@/components/ui/Button';
import { useDialogContext } from '@/contexts/DialogContext';
import { useLocale, useTranslations } from 'next-intl';
import DecisionPopup from './DecisionPopup';

export interface IAwardedFilm {
    category: string;
    tag: string;
    decision: string;
    imageSrc: string;
    description: string;
    country: string;
    director: string;
    title: {
        en: string;
        fr: string;
    };
    videoLink?: string;
}

export default function AwardedFilm({
    tag,
    decision,
    title,
    director,
    country,
    description,
    imageSrc,
    videoLink,
}: IAwardedFilm) {
    const t = useTranslations('Common');
    const locale = useLocale();
    const { enqueueDialog } = useDialogContext();

    const openJuryDecisionPopup = (e: React.MouseEvent) => {
        e.stopPropagation();
        enqueueDialog(<DecisionPopup text={decision} videoLink={videoLink} />);
    };

    return (
        <div className="flex justify-between items-stretch max-lg:flex-col py-5 lg:py-7 border-b border-white/50 w-full 2xl:gap-20 lg:gap-15 gap-5">
            <div className="w-full lg:w-1/6 flex flex-col justify-between">
                <span className="text-md lg:text-h6 uppercase text-white block flex-1 grow">
                    {tag}
                </span>
                {decision && (
                    <Button
                        className="hidden lg:flex mt-auto w-fit px-[10px] jury-statement-trigger"
                        variant="secondary"
                        title={t('juryDecision')}
                        onClick={openJuryDecisionPopup}
                    />
                )}
            </div>
            <div className="w-full lg:w-3/6 flex flex-col gap-5 text-white/50">
                <div className="flex flex-col gap-1 lg:gap-3 lg:text-h6 text-md uppercase">
                    <span className="text-white">{title[locale as keyof typeof title]}</span>
                </div>
                <div className="flex items-center flex-wrap gap-5 text-[12px] lg:text-p font-light font-helvetica-neue">
                    <span>{director}</span>
                    <span>{country}</span>
                </div>
                <p className="text-[12px] lg:text-md font-light font-helvetica-neue">
                    {description}
                </p>
            </div>
            <div className="flex flex-col w-full lg:w-2/6">
                <div className="relative h-[220px] sm:h-[400px] lg:h-[220px] w-full">
                    <Image
                        className="object-cover object-center h-full w-full"
                        src={imageSrc}
                        alt="Film"
                        fill
                        forceSrc
                    />
                </div>
                <Button
                    className="flex lg:hidden mt-5"
                    variant="secondary"
                    title={t('juryDecision')}
                    onClick={openJuryDecisionPopup}
                />
            </div>
        </div>
    );
}
